/**
 * Lightweight API response cache with TTL + request deduplication.
 * Caches GET requests to avoid redundant fetches.
 * Deduplicates concurrent identical requests.
 */
const cache = new Map();
const pendingRequests = new Map();

const DEFAULT_TTL = 30_000; // 30 seconds
const LONG_TTL = 120_000;   // 2 minutes

// Endpoints with longer cache TTL (rarely change)
const LONG_CACHE = ["/users/list", "/issuers", "/teams", "/document-templates"];

export function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

export function setCache(key, data, url) {
  const ttl = LONG_CACHE.some(p => url?.includes(p)) ? LONG_TTL : DEFAULT_TTL;
  cache.set(key, { data, expiresAt: Date.now() + ttl });
  // Evict old entries if cache grows too large
  if (cache.size > 150) {
    const now = Date.now();
    for (const [k, v] of cache) {
      if (now > v.expiresAt) cache.delete(k);
    }
  }
}

export function invalidateCache(pattern) {
  if (!pattern) { cache.clear(); return; }
  for (const key of cache.keys()) {
    if (key.includes(pattern)) cache.delete(key);
  }
}

export function clearAllCache() {
  cache.clear();
  pendingRequests.clear();
}

/**
 * Deduplicate concurrent identical GET requests.
 * If a request with the same key is already in-flight, returns the same promise.
 */
export function deduplicateRequest(key, requestFn) {
  const existing = pendingRequests.get(key);
  if (existing) return existing;
  
  const promise = requestFn().finally(() => {
    pendingRequests.delete(key);
  });
  pendingRequests.set(key, promise);
  return promise;
}
